import * as React from "react";
import { useState } from "react";
import {
    vorgangService,
    type VorgangPickerItem,
    type VorgangPickerTodo,
} from "../../../services/vorgangService";
import VorgangPickerAuswahl from "../picker/VorgangPickerAuswahl";
import { IcoX } from "./vg3Icons";
// `VorgangPickerAuswahl` holt seine Optik aus `.cal-vgd-*` in calendar.css
// und importiert sie NICHT selbst — ohne diese Zeile steht die Liste nackt da.
import "../../calendar/calendar.css";
import "./vorgangV3.css";

type Vorgang = VorgangPickerItem["vorgang"];

/**
 * „Todo eines anderen auswählen" (PO 05.09.2026, Zeichnung).
 *
 * Zwei Schritte, weil die Frage zwei Teile hat: aus WELCHEM Vorgang, und dann
 * WELCHES To-do. Beides kommt aus vorhandenen, serverseitig gefilterten
 * Endpunkten (`/vorgang/picker` und `/vorgang/:id/picker-todos`) — der Picker
 * zeigt nur, was der Nutzer ohnehin sehen darf.
 *
 * Das To-do wird VERKNÜPFT, nicht verschoben: es bleibt in seinem Vorgang zu
 * Hause und wird hier zusätzlich angezeigt.
 */
type Props = {
    /** Der Vorgang, der das fremde To-do zusätzlich zeigen soll. */
    vorgangId: number;
    contactId?: number | null;
    onClose: () => void;
    onVerknuepft: () => void | Promise<void>;
};

export const TodoVerknuepfenDialog: React.FC<Props> = ({
    vorgangId,
    contactId = null,
    onClose,
    onVerknuepft,
}) => {
    const [quelle, setQuelle] = useState<Vorgang | null>(null);
    const [todos, setTodos] = useState<VorgangPickerTodo[] | null>(null);
    const [busy, setBusy] = useState(false);
    const [fehler, setFehler] = useState<string | null>(null);

    const waehleVorgang = async (v: Vorgang) => {
        setQuelle(v);
        setTodos(null);
        setFehler(null);
        try {
            setTodos(await vorgangService.pickerTodos(v.id));
        } catch (err: any) {
            console.error("[TodoVerknuepfen] To-dos laden fehlgeschlagen", err);
            setFehler(err?.message ?? "To-dos konnten nicht geladen werden.");
            setTodos([]);
        }
    };

    const verknuepfe = async (todo: VorgangPickerTodo) => {
        if (busy) return;
        setBusy(true);
        setFehler(null);
        try {
            await vorgangService.verknuepfeTodo(vorgangId, todo.id);
            await onVerknuepft();
            onClose();
        } catch (err: any) {
            console.error("[TodoVerknuepfen] Verknüpfen fehlgeschlagen", err);
            setFehler(err?.message ?? "Verknüpfen fehlgeschlagen.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="kvp-rd vg3-modal-hg" role="presentation" onClick={onClose}>
            <div
                className="vg3-modal"
                role="dialog"
                aria-modal="true"
                aria-label="To-do eines anderen Vorgangs verknüpfen"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="vg3-modal-kopf">
                    <span className="vg3-eyebrow">To-do verknüpfen</span>
                    <button type="button" className="vg3-ibtn" title="Schließen" aria-label="Schließen" onClick={onClose}>
                        <IcoX size={16} />
                    </button>
                </div>

                {fehler && (
                    <div className="vg3-fehler" role="alert">
                        {fehler}
                    </div>
                )}

                <div className="vg3-modal-b">
                    {quelle == null ? (
                        <>
                            <p className="vg3-modal-hinweis">
                                Aus welchem Vorgang? Das To-do bleibt dort zu Hause und wird hier zusätzlich
                                angezeigt.
                            </p>
                            <VorgangPickerAuswahl
                                onPick={(v) => void waehleVorgang(v)}
                                contactId={contactId}
                                aktuelleVorgangId={vorgangId}
                                disabled={busy}
                            />
                        </>
                    ) : (
                        <>
                            <div className="vg3-modal-quelle">
                                <span className="vg3-modal-quelle-t">
                                    {quelle.vorgangNbr ? `${quelle.vorgangNbr} · ` : ""}
                                    {quelle.titel}
                                </span>
                                <button type="button" className="vg3-tbtn" onClick={() => setQuelle(null)}>
                                    anderer Vorgang
                                </button>
                            </div>

                            {todos == null ? (
                                <p className="vg3-modal-hinweis">To-dos werden geladen…</p>
                            ) : todos.length === 0 ? (
                                <p className="vg3-modal-hinweis">Dieser Vorgang hat keine To-dos.</p>
                            ) : (
                                <div className="vg3-modal-liste">
                                    {todos.map((t) => (
                                        <button
                                            key={t.id}
                                            type="button"
                                            className="vg3-modal-zeile"
                                            disabled={busy}
                                            onClick={() => void verknuepfe(t)}
                                        >
                                            <span className="vg3-modal-zeile-t">{t.titel || "(ohne Betreff)"}</span>
                                            <span className="vg3-badge">{t.status}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};
